import React, { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import PhoneCallIcon from '../../components/icons/PhoneCallIcon';
import HotjobsLogoIcon from '../../components/icons/HotjobsLogoIcon';
import { DownOutlined } from '@ant-design/icons';
import NavButton, { NavButtonVariant, NavButtonSize } from '../../components/navigation/NavButton';
import { Button, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import ProfileAvatar from './ProfileAvatar';
import BriefcaseIcon from '../../components/icons/BriefcaseIcon';
import AllServiceIcon from '../../components/icons/AllServiceIcon';
import PostJobIcon from '../../components/icons/PostJobIcon';
import FileTextOutlineIcon from '../../components/icons/FileTextOutlineIcon';
import { useUserInfo } from '../../connection/Application';
import { NotificationButton } from '../../components/NotificationButton/NotificationButton';

type TopBarItem = {
  text: string;
  icon: React.ReactNode;
};

type NavigationItem = {
  label: string;
  hasDropdown?: boolean;
  onClick?: () => void;
};

type NavActionItem = {
  label: string;
  variant?: NavButtonVariant;
  onClick?: () => void;
  className?: string;
  size?: NavButtonSize;
};

const topBarItems: TopBarItem[] = [
  {
    icon: <PhoneCallIcon className="w-6 h-6 text-black" />,
    text: '+94 77 360 360',
  },
];

const navigationItems: NavigationItem[] = [
  {
    label: 'Employers',
    hasDropdown: true,
  },
  {
    label: 'Jobseekers',
    hasDropdown: false,
  },
  {
    label: 'Foreign Jobs',
    hasDropdown: false,
  },
];

const navActionItems: NavActionItem[] = [
  {
    label: 'Post a Job',
    variant: 'link',
    size: 'sm',
  },
  {
    label: 'Upload CV',
    variant: 'link',
    size: 'sm',
  },
];

const employerServices = [
  {
    key: 'all-services',
    title: 'All Services',
    description: 'Explore all hiring tools in one place.',
  },
  {
    key: 'job-posting',
    title: 'Job Posting',
    description: 'Publish your job openings easily.',
  },
  {
    key: 'cv-search',
    title: 'CV Search',
    description: 'Find candidates with our smart search.',
  },
  {
    key: 'post-a-job',
    title: 'Post a Job',
    description: 'Create and upload a job listing quickly.',
  },
] as const;

const employerDropdownItems: MenuProps['items'] = employerServices.map(
  ({ key, title, description }) => {
    let Icon = BriefcaseIcon;
    if (key === 'all-services') Icon = AllServiceIcon;
    if (key === 'cv-search') Icon = FileTextOutlineIcon;
    if (key === 'post-a-job') Icon = PostJobIcon;

    return {
      key,
      className: '!p-0',
      label: (
        <div className="group flex items-start gap-3 rounded-lg px-3 py-3 transition-colors hover:bg-gray-50">
          <Icon className="h-6 w-6 shrink-0 text-gray-900 transition-colors group-hover:text-primaryRed" />
          <div className="flex flex-col text-left">
            <span className="text-[16px] font-semibold text-gray-900 transition-colors group-hover:text-primaryRed">
              {title}
            </span>
            <span className="text-sm text-gray-600 mt-1 group-hover:text-primaryRed">
              {description}
            </span>
          </div>
        </div>
      ),
    };
  },
);

const NAVBAR_FALLBACK_HEIGHT = 112;

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

export const CandidateUIV2NavBar = (): JSX.Element => {
  const user = useUserInfo();
  const router = useRouter();
  const navRef = useRef<HTMLElement | null>(null);
  const [isEmployerOpen, setIsEmployerOpen] = useState<boolean>(false);
  const [navHeight, setNavHeight] = useState<number>(NAVBAR_FALLBACK_HEIGHT);

  // Keep a spacer in sync with the navbar height so the fixed bar doesn't overlap content.
  useIsomorphicLayoutEffect(() => {
    const updateNavHeight = () => {
      if (navRef.current) {
        setNavHeight(navRef.current.getBoundingClientRect().height);
      }
    };

    updateNavHeight();
    window.addEventListener('resize', updateNavHeight);

    return () => window.removeEventListener('resize', updateNavHeight);
  }, []);

  const handleLogoClick = (): Promise<boolean> => router.push('/');
  return (
    <div className="relative">
      <header ref={navRef} className="fixed inset-x-0 top-0 z-50 w-full bg-white shadow-sm">
        <div className="w-full bg-gray-50 py-1">
          <div className="flex items-center justify-end gap-60 max-w-candidate-ui mx-auto px-4 md:px-6 ">
            <div className="inline-flex items-center gap-6">
              {topBarItems.map(({ icon, text }) => (
                <div key={text} className="inline-flex items-center gap-2">
                  {icon}
                  <div className="text-sm font-500">{text}</div>
                </div>
              ))}
              <div className="relative flex items-center gap-2">
                <img
                  className="w-6 h-4 object-cover"
                  alt="Language flag"
                  src="https://c.animaapp.com/mibc0aags5LdA5/img/image-1-1.png"
                />
                <div className="inline-flex items-center justify-center gap-1">
                  <div className="text-sm font-400 text-gray-600">English</div>
                  <DownOutlined className={'text-sm font-400 text-gray-600'} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-candidate-ui mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="inline-flex items-center gap-[60px]">
            <HotjobsLogoIcon
              className="h-8 w-auto translate-y-[-4px] cursor-pointer"
              aria-label="HotJobs logo"
              onClick={handleLogoClick}
            />
            <div className={'inline-flex items-center gap-[36px]'}>
              {navigationItems.map(({ label, hasDropdown, onClick }) =>
                hasDropdown && label === 'Employers' ? (
                  <Dropdown
                    key={label}
                    trigger={['hover']}
                    placement="bottomLeft"
                    open={isEmployerOpen}
                    onOpenChange={(open) => setIsEmployerOpen(open)}
                    dropdownRender={(menu) => (
                      <div
                        className="rounded-[12px] border  border-gray-200 p-5 bg-white shadow-lg"
                        style={{ width: 336, height: 412 }}>
                        {menu}
                      </div>
                    )}
                    menu={{
                      items: employerDropdownItems,
                      className: '!border-0 !shadow-none !p-2 overflow-y-auto h-full',
                    }}>
                    <NavButton
                      className={`flex items-center gap-1 ${
                        isEmployerOpen ? 'text-primaryRed' : ''
                      }`}
                      onClick={onClick}
                      aria-haspopup>
                      {label}
                      <DownOutlined
                        className={`text-sm ml-2 transition-colors duration-150 ${
                          isEmployerOpen ? 'text-primaryRed' : 'text-gray-600'
                        } group-hover:text-primaryRed`}
                      />
                    </NavButton>
                  </Dropdown>
                ) : (
                  <NavButton
                    key={label}
                    className="flex items-center gap-1"
                    onClick={onClick}
                    aria-haspopup={hasDropdown || undefined}>
                    {label}
                    {hasDropdown && (
                      <DownOutlined className="text-sm text-gray-600 ml-2 transition-colors duration-150 group-hover:text-primaryRed" />
                    )}
                  </NavButton>
                ),
              )}
            </div>
          </div>
          <div className={'inline-flex items-center gap-[36px]'}>
            {!user &&
              navActionItems.map(({ label, variant, onClick, className, size }) => (
                <NavButton
                  key={label}
                  variant={variant}
                  size={size}
                  onClick={onClick}
                  className={className}>
                  {label}
                </NavButton>
              ))}
            {!user && (
              <div className="inline-flex items-center gap-2">
                <Button
                  className="group inline-flex items-center justify-center px-4 text-[14px] font-semibold text-gray-700 border border-solid border-[#d5d6da] shadow-shadow-xs rounded-lg !h-10 transition-colors hover:text-primary w-full sm:w-auto"
                  onClick={() => router.push('/login')}>
                  Log In
                </Button>
                <Button
                  type="primary"
                  className="inline-flex items-center justify-center px-4 text-[14px] font-semibold rounded-lg !h-10 w-full sm:w-auto"
                  onClick={() => router.push('/sign-up')}>
                  Register Now
                </Button>
              </div>
            )}
            {user && (
              <div className="inline-flex items-center gap-6">
                <Button className="group inline-flex items-center justify-center px-4 text-[14px] font-semibold text-gray-700 border border-solid border-[#d5d6da] shadow-shadow-xs rounded-lg !h-10 transition-colors hover:text-primary w-full sm:w-auto">
                  Upload CV
                </Button>
                <Button className="group inline-flex items-center justify-center px-4 text-[14px] font-semibold text-primary border border-solid border-primary shadow-shadow-xs rounded-lg !h-10 transition-colors hover:text-primary w-full sm:w-auto">
                  Post a Job
                </Button>
                <NotificationButton darkMode={false} />
                <ProfileAvatar
                  src={
                    user?.picture ??
                    `https://ui-avatars.com/api/?name=${user.name}&background=E5E7EB&color=6B7280&size=160`
                  }
                  size={40}
                  showBadge
                />
              </div>
            )}
          </div>
        </div>
        <div className="w-full h-px bg-gray-200" />
      </header>
      <div aria-hidden style={{ height: navHeight }} />
    </div>
  );
};
